import { useCallback, useEffect, useRef, useState } from "react";
import { api } from "../lib/api";
import { resolveUsageOrgId } from "../lib/usage-org";

export interface OrgUsage {
  periodStart: string;
  periodEnd: string;
  /** Für wen gezählt wird */
  orgName: string;
  /** true, wenn wir dieser Organisation nicht angehören (Plattform-Admin im Kundenkanal) */
  foreign: boolean;
  used: number;
  quota: number | null;
  visibility: "members" | "admins";
}

/** Minutentakt reicht: Vorgänge entstehen im Minuten- bis Stundenabstand. */
const REFRESH_MS = 60_000;

/**
 * Verbrauch der Organisation für den Balken in der Seitenleiste (KOL-94).
 *
 * Liefert null, solange nichts anzuzeigen ist — kein Kanalbezug bei mehreren
 * Organisationen, oder der Server verweigert die Zahlen (Sichtbarkeit „nur
 * Admins"). Beides ist ein normaler Zustand, kein Fehler: der Balken bleibt
 * dann einfach weg.
 */
export function useOrgUsage(activeChannelOrgId: string | null): OrgUsage | null {
  const [userOrgIds, setUserOrgIds] = useState<string[]>([]);
  const [usage, setUsage] = useState<OrgUsage | null>(null);
  const lastOrgId = useRef<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .get<{ organizations: Array<{ id: string }> }>("/orgs")
      .then((res) => {
        if (!cancelled) setUserOrgIds(res.organizations.map((o) => o.id));
      })
      .catch(() => {
        if (!cancelled) setUserOrgIds([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const orgId = resolveUsageOrgId({ activeChannelOrgId, userOrgIds });

  const load = useCallback(async (id: string) => {
    try {
      const res = await api.get<OrgUsage>(`/orgs/${id}/usage`);
      setUsage(res);
    } catch {
      // 403 bei Sichtbarkeit "nur Admins", 404 bei fremder Organisation —
      // in beiden Fällen zeigen wir schlicht nichts an.
      setUsage(null);
    }
  }, []);

  useEffect(() => {
    if (!orgId) {
      setUsage(null);
      lastOrgId.current = null;
      return;
    }
    // Beim Wechsel der Organisation erst die alten Zahlen verwerfen, sonst
    // stünde für einen Moment der Verbrauch der vorigen Organisation da.
    if (lastOrgId.current !== orgId) {
      setUsage(null);
      lastOrgId.current = orgId;
    }
    void load(orgId);
    const timer = setInterval(() => void load(orgId), REFRESH_MS);
    return () => clearInterval(timer);
  }, [orgId, load]);

  return usage;
}
